/**
 * SiteRederiveModal — the preview + consent gate for "Re-derive site from
 * grid". NOTHING is written until Apply; the tally and the per-field clear
 * checkboxes ARE the safety contracts made visible (c6, 2026-09-03 +
 * same-day review revisions):
 *  - human-tagged values: reported as protected, never touchable here;
 *  - untagged disagreements (the pre-provenance population): applied only
 *    with the accept tier ticked — the copy says what that means;
 *  - clears: SHARE the accept authorization (deletion is the most
 *    destructive tier), then per-field opt-in with a value histogram —
 *    frequency is what separates uniform junk from a hand-typed field;
 *  - grid-quality hold-out: faults deriving from flagged strings
 *    (unparseable label, stray ⚠ direction, ambiguous, overlapping cells)
 *    are excluded from the destructive tiers unless explicitly included.
 * The whole run lands as ONE undoable operation (executor in the ticket).
 */

import { useState } from 'react';
import type { SiteRederivePlan } from '../../domain/topology/planSiteRederive';
import { Button } from '../common';
import './SiteRederiveModal.css';

interface Props {
  siteName: string;
  plan: SiteRederivePlan;
  onApply: (opts: { acceptTier: boolean; includeFlagged: boolean; clearFields: Set<string> }) => void;
  onCancel: () => void;
}

/** "\"INV\" ×43, \"3DCCB\" ×2, +7 others" — top-N of a value histogram. */
function histogramLine(histogram: Record<string, number>, topN = 8): string {
  const entries = Object.entries(histogram).sort((a, b) => b[1] - a[1]);
  const shown = entries.slice(0, topN).map(([v, n]) => `"${v}" ×${n}`).join(', ');
  const rest = entries.length - topN;
  return rest > 0 ? `${shown}, +${rest} other value${rest === 1 ? '' : 's'}` : shown;
}

export default function SiteRederiveModal({ siteName, plan, onApply, onCancel }: Props) {
  const [acceptTier, setAcceptTier] = useState(true);
  const [includeFlagged, setIncludeFlagged] = useState(false);
  const [clearTicks, setClearTicks] = useState<Set<string>>(new Set());
  const t = plan.tally;
  const clearFields = Object.entries(t.clears);
  const flaggedClears = clearFields.reduce(
    (n, [k, v]) => n + (clearTicks.has(k) ? v.flaggedCount : 0), 0);
  const hasFlagged = t.flaggedAccepts > 0
    || clearFields.some(([, v]) => v.flaggedCount > 0);
  const willWrite =
    t.fills + t.retags
    + (acceptTier ? t.accepts + (includeFlagged ? t.flaggedAccepts : 0) : 0)
    + (acceptTier
      ? clearFields.reduce((n, [k, v]) => n + (clearTicks.has(k) ? v.count : 0), 0)
        - (includeFlagged ? 0 : flaggedClears)
      : 0);

  return (
    <div className="srm__backdrop">
      <div className="srm">
        <h3 className="srm__title">Re-derive {siteName} from grid</h3>
        <p className="srm__warn">
          Run this only on a site whose grid you have verified against the
          imagery. Everything below applies as <b>one undoable operation</b> —
          nothing is written until you press Apply.
        </p>

        <div className="srm__rows">
          <div className="srm__row">
            <span className="srm__count">{t.fills}</span> empty fields fill from the grid
          </div>
          <div className="srm__row">
            <span className="srm__count">{t.retags}</span> machine-written values update to the corrected grid
          </div>
          <label className="srm__row srm__row--tick">
            <input type="checkbox" checked={acceptTier} onChange={(e) => setAcceptTier(e.target.checked)} />
            <span className="srm__count">{t.accepts}</span> stored values that disagree with the grid are replaced
            <span className="srm__hint">(values from before provenance tracking — your sampling of this site is the authorization)</span>
          </label>
          {hasFlagged && (
            <label className={`srm__row srm__row--tick srm__row--flagged${acceptTier ? '' : ' srm__row--disabled'}`}>
              <input
                type="checkbox"
                checked={includeFlagged}
                disabled={!acceptTier}
                onChange={(e) => setIncludeFlagged(e.target.checked)}
              />
              <span className="srm__count">{t.flaggedAccepts}</span> more overwrites held back — their faults derive
              from strings with grid-quality flags (unparseable label, stray ⚠ direction, ambiguous, overlapping cells)
              <span className="srm__hint">tick only if you have reviewed those strings in the Module Grid panel</span>
            </label>
          )}
          <div className="srm__row srm__row--protected">
            <span className="srm__count">{t.humanKept}</span> human-set values kept — never touched by this tool
          </div>
          <div className="srm__row srm__row--muted">
            {t.agree} already agree · {t.offGrid} off-grid untouched · {t.faults} faults scanned
          </div>
        </div>

        {clearFields.length > 0 && (
          <div className={`srm__clears${acceptTier ? '' : ' srm__row--disabled'}`}>
            <div className="srm__clears-title">
              Fields with stored values the grid defines nothing for — tick to clear (per field
              {acceptTier ? '' : '; needs the replace authorization above'}):
            </div>
            {clearFields.map(([field, info]) => (
              <label key={field} className="srm__row srm__row--tick">
                <input
                  type="checkbox"
                  checked={clearTicks.has(field)}
                  disabled={!acceptTier}
                  onChange={(e) => {
                    const next = new Set(clearTicks);
                    if (e.target.checked) next.add(field); else next.delete(field);
                    setClearTicks(next);
                  }}
                />
                <span className="srm__count">{info.count}</span> {field}
                <span className="srm__hint">{histogramLine(info.histogram)}</span>
                {info.stringMode > 0 && (
                  <span className="srm__hint">
                    {info.stringMode} of these are string-level faults — a string fault carries no module, so
                    this clear is expected hygiene
                  </span>
                )}
                {info.flaggedCount > 0 && !includeFlagged && (
                  <span className="srm__hint">{info.flaggedCount} on flagged strings stay held back</span>
                )}
              </label>
            ))}
          </div>
        )}

        <div className="srm__actions">
          <Button variant="secondary" size="sm" onClick={onCancel}>Cancel</Button>
          <Button
            variant="primary"
            size="sm"
            disabled={willWrite === 0}
            onClick={() => onApply({ acceptTier, includeFlagged, clearFields: clearTicks })}
          >
            Apply {willWrite} change{willWrite === 1 ? '' : 's'}
          </Button>
        </div>
      </div>
    </div>
  );
}
